// Slices module: create/update/destroy magnetic field slices (XY, ZY, ZX).
//
// Three axes, one implementation. AXIS_SPECS encodes the per-axis differences
// (which two grid axes span the canvas, which axis is "through-plane", the
// canvas U-flip for ZX, and the Three.js mesh rotation). Every per-axis op
// (create, redraw, move) goes through the table.
//
// params contract — keys read from the reactive store:
//   colormap, colormapMin, colormapMax, transparentBounds
//   sliceOpacity, sliceResolution, slicePixelated
//   sliceMaterial, sliceDepthWrite, sliceDepthTest, sliceDoubleSide
//   sliceAlphaTest, sliceMetalness, sliceRoughness, sliceRenderOrder
//   sliceCastShadow, sliceReceiveShadow

import * as THREE from 'three';
import { physicalToThreeJS } from '@adapters/coordinates';
import { getFieldValue } from '../physics/probe';
import { SLICE_DEFAULTS } from '../config/defaults';
import { strengthToRGBA, colorOptsFromViewerCtx, colorOptsFromBinding } from '../physics/colormap';
import type { StrengthToColorOpts } from '../physics/types';
import { debugLog } from '@adapters/debug';
import { params as storeParams } from '../store/instance';
import { resolveDomainBounds, isOutsideDomain, type ResolvedDomain } from '../store/selections';
import type { ViewerContext, SliceAxisKey, SliceState, GridState } from '../app/context';
import type { ColormapBinding, ParamsState } from '../store/schema';

type Axis = 'x' | 'y' | 'z';
type RotationKey = 'x' | 'y' | 'z';

interface AxisSpec {
    u: Axis;
    v: Axis;
    sliceAxis: Axis;
    flipU: boolean;
    rotation: Partial<Record<RotationKey, number>>;
}

const AXIS_SPECS: Record<SliceAxisKey, AxisSpec> = {
    XY: { u: 'x', v: 'y', sliceAxis: 'z', flipU: false, rotation: { x: Math.PI / 2, z: Math.PI / 2 } },
    ZY: { u: 'y', v: 'z', sliceAxis: 'x', flipU: false, rotation: {} },
    // ZX mirrors U so the canvas's left edge (j=0) maps to physical +X (matches
    // the mesh's y=π/2 rotation; without the flip the texture appears reversed).
    ZX: { u: 'x', v: 'z', sliceAxis: 'y', flipU: true,  rotation: { y: Math.PI / 2 } },
};

const AXIS_LIST: SliceAxisKey[] = ['XY', 'ZY', 'ZX'];

const gridRange = (grid: GridState, axis: Axis): [number, number] => (
    axis === 'x' ? [grid.xMin, grid.xMax] :
    axis === 'y' ? [grid.yMin, grid.yMax] :
                   [grid.zMin, grid.zMax]
);
const gridMid = (grid: GridState, axis: Axis): number => { const [a, b] = gridRange(grid, axis); return (a + b) / 2; };

function makeSliceMaterial(texture: THREE.Texture, params: ParamsState): THREE.MeshStandardMaterial | THREE.MeshLambertMaterial {
    const opacity = (typeof params.sliceOpacity === 'number') ? params.sliceOpacity : 1.0;
    const depthWrite = (typeof params.sliceDepthWrite === 'boolean') ? params.sliceDepthWrite : (opacity >= 1.0);
    const depthTest = (typeof params.sliceDepthTest === 'boolean') ? params.sliceDepthTest : true;
    const doubleSide = (typeof params.sliceDoubleSide === 'boolean') ? params.sliceDoubleSide : true;
    const useStandard = (params.sliceMaterial || 'lambert') === 'standard';
    const side = doubleSide ? THREE.DoubleSide : THREE.FrontSide;
    const alphaTest = (typeof params.sliceAlphaTest === 'number') ? params.sliceAlphaTest : 0.0;
    const transparentBounds = !!(params && params.transparentBounds);
    // Stage 5: a domain mask paints out-of-domain pixels at alpha=0, so the
    // slice material needs `transparent` whenever a non-full domain is set —
    // otherwise the framebuffer composites those pixels as opaque black.
    const domainMasked = !!params.slicesDomain && params.slicesDomain !== 'full';
    const needsTransparency = opacity < 1.0 || alphaTest > 0.0 || transparentBounds || domainMasked;
    if (useStandard) {
        const metalness = (typeof params.sliceMetalness === 'number') ? params.sliceMetalness : 0.05;
        const roughness = (typeof params.sliceRoughness === 'number') ? params.sliceRoughness : 0.9;
        return new THREE.MeshStandardMaterial({ map: texture, transparent: needsTransparency, opacity, depthWrite, depthTest, side, metalness, roughness, alphaTest });
    }
    const emissive = new THREE.Color(0x000000);
    return new THREE.MeshLambertMaterial({ map: texture, transparent: needsTransparency, opacity, side, depthWrite, depthTest, alphaTest, emissive });
}

// ─── per-slice ops ─────────────────────────────────────────────────────────

/**
 * Build a single slice (canvas, texture, material, mesh) for the given axis.
 * Optional `slicePos` overrides the through-plane position (defaults to grid
 * midpoint along sliceAxis).
 */
export function createSlice(axis: SliceAxisKey, viewer: ViewerContext, params: ParamsState, slicePos?: number): SliceState {
    const spec = AXIS_SPECS[axis];
    if (!spec) throw new Error(`createSlice: unknown axis ${axis}`);
    const grid = viewer.grid;
    const scene = viewer.three.scene;
    if (!scene) throw new Error('createSlice: scene not initialised');

    const [uMin, uMax] = gridRange(grid, spec.u);
    const [vMin, vMax] = gridRange(grid, spec.v);
    const uRange = uMax - uMin;
    const vRange = vMax - vMin;

    const canvas = document.createElement('canvas');
    const requested = (typeof params.sliceResolution === 'number') ? params.sliceResolution : SLICE_DEFAULTS.resolution;
    const width = Math.max(1, Math.floor((requested >= 2 && isFinite(requested)) ? requested : SLICE_DEFAULTS.resolution));
    const height = (uRange > 0 && isFinite(uRange)) ? Math.max(1, Math.floor(width * vRange / uRange)) : width;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    const texture = new THREE.CanvasTexture(canvas);
    const pixelated = !!params.slicePixelated;
    texture.magFilter = pixelated ? THREE.NearestFilter : THREE.LinearFilter;
    texture.minFilter = pixelated ? THREE.NearestFilter : THREE.LinearFilter;
    texture.generateMipmaps = !pixelated;
    texture.needsUpdate = true;

    const material = makeSliceMaterial(texture, params);
    const geometry = new THREE.PlaneGeometry(uRange, vRange);
    const mesh = new THREE.Mesh(geometry, material);
    for (const [rotAxis, rad] of Object.entries(spec.rotation)) {
        if (typeof rad === 'number') mesh.rotation[rotAxis as RotationKey] = rad;
    }

    const pos: Record<Axis, number> = { x: gridMid(grid, 'x'), y: gridMid(grid, 'y'), z: gridMid(grid, 'z') };
    if (typeof slicePos === 'number' && isFinite(slicePos)) pos[spec.sliceAxis] = slicePos;
    mesh.position.copy(physicalToThreeJS(pos.x, pos.y, pos.z));

    if (typeof params.sliceRenderOrder === 'number') mesh.renderOrder = params.sliceRenderOrder;
    mesh.castShadow = !!params.sliceCastShadow;
    mesh.receiveShadow = !!params.sliceReceiveShadow;
    scene.add(mesh);
    return { mesh, canvas, context, texture };
}

/**
 * Rasterize the field onto the slice canvas at `slicePos` along the
 * through-plane axis. The colormap is supplied either as a precomputed
 * `StrengthToColorOpts` (post-binding render path) or derived from the
 * legacy global params (transition path).
 */
export function updateSliceData(axis: SliceAxisKey, viewer: ViewerContext, paramsOrOpts: ParamsState | StrengthToColorOpts, state: SliceState, slicePos: number): void {
    const spec = AXIS_SPECS[axis];
    if (!spec) throw new Error(`updateSliceData: unknown axis ${axis}`);
    const grid = viewer.grid;
    const { canvas, context, texture } = state;
    if (!canvas || !context) return;
    const [uMin, uMax] = gridRange(grid, spec.u);
    const [vMin, vMax] = gridRange(grid, spec.v);
    const uRange = uMax - uMin;
    const vRange = vMax - vMin;

    const imageData = context.createImageData(canvas.width, canvas.height);
    const data = imageData.data;
    const sample: Record<Axis, number> = { x: 0, y: 0, z: 0 };
    sample[spec.sliceAxis] = slicePos;
    // Hoist color opts: identical across every pixel of one rasterization.
    // The shape distinction is ParamsState (legacy) vs StrengthToColorOpts
    // (already resolved from a ColormapBinding).
    const opts: StrengthToColorOpts = isStrengthOpts(paramsOrOpts)
        ? paramsOrOpts
        : colorOptsFromViewerCtx(viewer, paramsOrOpts);
    // Resolve the current slice domain. Hoist branch outside the loop so
    // the 'full' case (~99% of paints) pays zero per-pixel cost.
    const domain: ResolvedDomain = resolveDomainBounds(storeParams.selections, storeParams.slicesDomain);
    const masked = domain.kind !== 'full';

    for (let i = 0; i < canvas.height; i++) {
        const v = vMin + (i / canvas.height) * vRange;
        sample[spec.v] = v;
        for (let j = 0; j < canvas.width; j++) {
            const u = spec.flipU
                ? uMax - (j / canvas.width) * uRange
                : uMin + (j / canvas.width) * uRange;
            sample[spec.u] = u;
            const idx = (i * canvas.width + j) * 4;
            if (masked && isOutsideDomain(sample.x, sample.y, sample.z, domain)) {
                data[idx] = 0; data[idx + 1] = 0; data[idx + 2] = 0; data[idx + 3] = 0;
                continue;
            }
            const val = getFieldValue(viewer, sample.x, sample.y, sample.z);
            const c = strengthToRGBA(val, opts);
            data[idx] = c.r; data[idx + 1] = c.g; data[idx + 2] = c.b; data[idx + 3] = c.a;
        }
    }
    context.putImageData(imageData, 0, 0);
    if (texture) texture.needsUpdate = true;
}

function isStrengthOpts(x: ParamsState | StrengthToColorOpts): x is StrengthToColorOpts {
    return typeof (x as StrengthToColorOpts).defs !== 'undefined' && typeof (x as StrengthToColorOpts).name === 'string';
}

/** Convenience wrapper used by the colormapSync fan-out when redrawing a
 *  slice for a specific binding. */
export function updateSliceForBinding(axis: SliceAxisKey, viewer: ViewerContext, binding: ColormapBinding, state: SliceState, slicePos: number): void {
    updateSliceData(axis, viewer, colorOptsFromBinding(viewer, binding), state, slicePos);
}

/**
 * Move the slice mesh to a new through-plane position and redraw the canvas.
 */
export function updateSlicePosition(axis: SliceAxisKey, viewer: ViewerContext, params: ParamsState, slicePos: number): void {
    const spec = AXIS_SPECS[axis];
    if (!spec) throw new Error(`updateSlicePosition: unknown axis ${axis}`);
    const state = viewer.objects.slices.state[axis];
    if (!state || !state.canvas || !state.mesh) return;

    const grid = viewer.grid;
    const pos: Record<Axis, number> = { x: gridMid(grid, 'x'), y: gridMid(grid, 'y'), z: gridMid(grid, 'z') };
    pos[spec.sliceAxis] = slicePos;
    state.mesh.position.copy(physicalToThreeJS(pos.x, pos.y, pos.z));

    updateSliceData(axis, viewer, params, state, slicePos);
    debugLog(viewer, `${axis} moved`, slicePos);
}

// ─── orchestration over all three axes ─────────────────────────────────────

const VISIBILITY_PARAM: Record<SliceAxisKey, keyof ParamsState> = { XY: 'showFieldSlice', ZY: 'showFieldSliceZY', ZX: 'showFieldSliceZX' };
// Through-plane position for each slice axis: XY moves along z, ZY along x, ZX along y.
const POSITION_PARAM: Record<SliceAxisKey, keyof ParamsState> = { XY: 'sliceZ', ZY: 'sliceZY', ZX: 'sliceZX' };

export { POSITION_PARAM };

export function createAllSlices(viewer: ViewerContext, params: ParamsState): void {
    AXIS_LIST.forEach((axis) => {
        const slicePos = params[POSITION_PARAM[axis]] as number;
        const state = createSlice(axis, viewer, params, slicePos);
        viewer.objects.slices.state[axis] = state;
        updateSliceData(axis, viewer, params, state, slicePos);
    });
    applyVisibility(viewer, params);
}

export function applyVisibility(viewer: ViewerContext, params: ParamsState): void {
    AXIS_LIST.forEach((axis) => {
        const s = viewer.objects.slices.state[axis];
        if (s && s.mesh) s.mesh.visible = !!params[VISIBILITY_PARAM[axis]];
    });
}

export function destroyAllSlices(viewer: ViewerContext): void {
    const scene = viewer.three.scene;
    AXIS_LIST.forEach((axis) => {
        const s = viewer.objects.slices.state[axis];
        if (s && s.mesh) {
            try {
                if (s.mesh.parent && scene) scene.remove(s.mesh);
                if (s.mesh.geometry && s.mesh.geometry.dispose) s.mesh.geometry.dispose();
                if (s.mesh.material) {
                    if (Array.isArray(s.mesh.material)) s.mesh.material.forEach((m) => { if (m && m.dispose) m.dispose(); });
                    else if (s.mesh.material.dispose) s.mesh.material.dispose();
                }
                if (s.texture && s.texture.dispose) s.texture.dispose();
            } catch { /* swallow */ }
        }
        viewer.objects.slices.state[axis] = { mesh: null, canvas: null, context: null, texture: null };
    });
}

export function rebuildAllSlices(viewer: ViewerContext, params: ParamsState): void {
    destroyAllSlices(viewer);
    createAllSlices(viewer, params);
}

/**
 * Restore any slice mesh whose parent was nulled (e.g. when volume was
 * temporarily disabled). Called from destroyVolume to re-add slices to the
 * scene without recreating their canvases.
 */
export function reattachOrphanedSlices(viewer: ViewerContext): void {
    const scene = viewer.three.scene;
    if (!scene) return;
    AXIS_LIST.forEach((axis) => {
        const s = viewer.objects.slices.state[axis];
        if (s && s.mesh && !s.mesh.parent) scene.add(s.mesh);
    });
}

/**
 * Flip the slice meshes' material.transparent flag in response to a
 * transparentBounds toggle.
 */
export function applyTransparentBounds(viewer: ViewerContext, params: ParamsState): void {
    AXIS_LIST.forEach((axis) => {
        const s = viewer.objects.slices.state[axis];
        if (!s || !s.mesh || !s.mesh.material) return;
        const mat = Array.isArray(s.mesh.material) ? s.mesh.material[0] : s.mesh.material;
        if (!mat) return;
        const sliceOpacity = (typeof params.sliceOpacity === 'number') ? params.sliceOpacity : 1.0;
        const alphaTest = mat.alphaTest || 0;
        mat.transparent = sliceOpacity < 1.0 || alphaTest > 0 || !!params.transparentBounds;
        mat.needsUpdate = true;
    });
}

/**
 * Apply Nearest vs Linear texture filtering to every slice.
 */
export function applyTextureFilters(viewer: ViewerContext, params: ParamsState): void {
    const pixelated = !!params.slicePixelated;
    const filter = pixelated ? THREE.NearestFilter : THREE.LinearFilter;
    AXIS_LIST.forEach((axis) => {
        const s = viewer.objects.slices.state[axis];
        if (!s || !s.texture) return;
        s.texture.magFilter = filter;
        s.texture.minFilter = filter;
        s.texture.generateMipmaps = !pixelated;
        s.texture.needsUpdate = true;
    });
}

// ─── display-settings (material/shadow/opacity) ────────────────────────────

/**
 * Apply current params.slice* settings to all three slice meshes.
 * Swaps Lambert↔Standard material in-place when params.sliceMaterial changes,
 * preserving the texture map.
 */
export function applyDisplaySettings(viewer: ViewerContext, params: ParamsState): void {
    AXIS_LIST.forEach((axis) => {
        const s = viewer.objects.slices.state[axis];
        if (!s || !s.mesh) return;
        const mesh = s.mesh;
        const matRaw = mesh.material;
        const mat = Array.isArray(matRaw) ? matRaw[0] : matRaw;
        const wantStandard = (params.sliceMaterial || 'standard') === 'standard';
        const isStandard = mat instanceof THREE.MeshStandardMaterial;
        let material: THREE.MeshStandardMaterial | THREE.MeshLambertMaterial;
        if (wantStandard !== isStandard) {
            const map = (mat && 'map' in mat ? mat.map : null) as THREE.Texture | null;
            if (mat && mat.dispose) mat.dispose();
            material = wantStandard
                ? new THREE.MeshStandardMaterial({ map })
                : new THREE.MeshLambertMaterial({ map });
            mesh.material = material;
        } else {
            material = mat as THREE.MeshStandardMaterial | THREE.MeshLambertMaterial;
        }
        const opacity = (typeof params.sliceOpacity === 'number') ? params.sliceOpacity : material.opacity;
        const alphaTest = (typeof params.sliceAlphaTest === 'number') ? params.sliceAlphaTest : 0.0;
        const domainMasked = !!params.slicesDomain && params.slicesDomain !== 'full';
        material.opacity = opacity;
        material.transparent = opacity < 1.0 || alphaTest > 0.0 || domainMasked;
        material.alphaTest = alphaTest;
        if (typeof params.sliceDepthWrite === 'boolean') material.depthWrite = !!params.sliceDepthWrite;
        if (typeof params.sliceDepthTest === 'boolean') material.depthTest = !!params.sliceDepthTest;
        material.side = params.sliceDoubleSide ? THREE.DoubleSide : THREE.FrontSide;
        if (material instanceof THREE.MeshStandardMaterial && typeof params.sliceMetalness === 'number') material.metalness = params.sliceMetalness;
        if (material instanceof THREE.MeshStandardMaterial && typeof params.sliceRoughness === 'number') material.roughness = params.sliceRoughness;
        material.needsUpdate = true;
        if (typeof params.sliceRenderOrder === 'number') mesh.renderOrder = params.sliceRenderOrder;
        mesh.castShadow = !!params.sliceCastShadow;
        mesh.receiveShadow = !!params.sliceReceiveShadow;
    });
}
